import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, IconButton, Avatar } from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import proxy from '../../api/axios';
import { formatDateTime } from '../../common/utils';
import {
  ACCENT,
  AdminCard,
  AdminEmpty,
  AdminLoading,
  AdminPage,
  AdminStatGrid,
  MetaChip,
  useAdminPageTheme,
} from './adminUi';

const NeetTestAttemptsPage = () => {
  const { border, muted, isDark } = useAdminPageTheme();
  const navigate = useNavigate();
  const { id: testId } = useParams();
  const [test, setTest] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAttempts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await proxy.get(`/neet-tests/admin/${testId}/attempts`);
      setTest(data.test);
      setAttempts(Array.isArray(data.attempts) ? data.attempts : []);
    } catch (error) {
      toast.error('Failed to load attempts');
      navigate('/admin/manage-neet-tests');
    } finally {
      setLoading(false);
    }
  }, [testId, navigate]);

  useEffect(() => {
    fetchAttempts();
  }, [fetchAttempts]);

  const stats = useMemo(() => {
    const submitted = attempts.filter((a) => a.submitted);
    const avgScore = submitted.length
      ? (submitted.reduce((sum, a) => sum + (a.score || 0), 0) / submitted.length).toFixed(1)
      : '—';
    return { total: attempts.length, submitted: submitted.length, inProgress: attempts.length - submitted.length, avgScore };
  }, [attempts]);

  if (loading) {
    return (
      <AdminPage>
        <AdminLoading />
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      <Helmet>
        <title>Attempts | {test?.title || 'NEET Test'} | Admin | CrafterHack</title>
      </Helmet>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={() => navigate('/admin/manage-neet-tests')} sx={{ border: `1px solid ${border}` }}>
          <ArrowBackRoundedIcon />
        </IconButton>
        <Box>
          <Typography sx={{ fontWeight: 900, fontSize: '1.3rem', letterSpacing: '-0.03em' }}>
            {test?.title}
          </Typography>
          <Typography variant="body2" sx={{ color: muted }}>
            Max score {test?.totalMarks} · All user attempts for this test
          </Typography>
        </Box>
      </Box>

      <AdminStatGrid
        items={[
          { label: 'TOTAL ATTEMPTS', value: stats.total },
          { label: 'SUBMITTED', value: stats.submitted, color: '#1a7f37' },
          { label: 'IN PROGRESS', value: stats.inProgress, color: '#d97706' },
          { label: 'AVG SCORE', value: stats.avgScore, color: ACCENT },
        ]}
      />

      {attempts.length === 0 ? (
        <AdminEmpty title="No attempts yet" description="Nobody has started this test." />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {attempts.map((attempt) => (
            <AdminCard
              key={attempt._id}
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                <Avatar sx={{ width: 36, height: 36, bgcolor: ACCENT }}>
                  <PersonOutlineIcon fontSize="small" />
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.9rem' }}>
                    {attempt.userId?.name || 'Unknown user'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: muted, fontSize: '0.78rem' }}>
                    {attempt.userId?.email}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <MetaChip
                  label={attempt.submitted ? 'Submitted' : 'In progress'}
                  color={attempt.submitted ? '#1a7f37' : '#d97706'}
                  isDark={isDark}
                />
                {attempt.autoSubmitted && <MetaChip label="Auto-submitted" color="#8b949e" isDark={isDark} />}
                <Typography sx={{ fontWeight: 800, fontSize: '0.9rem' }}>
                  {attempt.submitted ? `${attempt.score} / ${attempt.maxScore}` : '—'}
                </Typography>
                {attempt.submitted && (
                  <Typography variant="body2" sx={{ color: muted, fontSize: '0.78rem' }}>
                    ✓ {attempt.correctCount} · ✗ {attempt.incorrectCount} · — {attempt.unattemptedCount}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ color: muted, fontSize: '0.78rem' }}>
                  {attempt.submittedAt ? formatDateTime(attempt.submittedAt).date : formatDateTime(attempt.startedAt).date}
                </Typography>
              </Box>
            </AdminCard>
          ))}
        </Box>
      )}
    </AdminPage>
  );
};

export default NeetTestAttemptsPage;
